import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import FilmCard from "@/components/base/FilmCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { getAllFilms, getCategoryMeta, searchFilms, type Film } from "@/lib/films";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

type SortKey = "updated" | "release";

export default function Category() {
  const { type = "all" } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";

  const [regions, setRegions] = useState<string[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [genres, setGenres] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const meta = getCategoryMeta(type);
  const isAll = type === "all" || !meta;

  const categoryName = isAll ? "全部影视" : meta.name;
  const pageTitle = keyword
    ? `一路向西影视作品 - “${keyword}”搜索结果`
    : isAll
      ? "一路向西影视作品 - 全部影视"
      : `一路向西影视作品 - ${meta.name}分类`;
  const pageDescription = keyword
    ? `在“一路向西”平台中搜索与“${keyword}”相关的影视作品，查看剧情介绍、角色分析与影视动态。`
    : isAll
      ? "浏览一路向西平台全部影视作品，涵盖电影、电视剧与网络剧分类。"
      : `浏览一路向西平台${meta.name}分类影视作品，筛选您的喜爱影片。`;

  useSeo({
    title: pageTitle,
    description: pageDescription,
    keywords: `一路向西,${categoryName},影视作品,剧情介绍,角色分析,影视动态`,
  });

  const baseList = useMemo(() => {
    let list: Film[] = getAllFilms();
    if (!isAll) list = list.filter((film) => film.category === type);
    if (keyword) {
      const matched = searchFilms(keyword);
      list = list.filter((film) => matched.some((item) => item.id === film.id));
    }
    return list;
  }, [type, isAll, keyword]);

  const filtered = useMemo(() => {
    let list = baseList;
    if (regions.length) list = list.filter((film) => regions.includes(film.region));
    if (years.length) list = list.filter((film) => years.includes(film.year));
    if (genres.length) list = list.filter((film) => film.genres.some((genre) => genres.includes(genre)));
    return [...list].sort((a, b) => {
      if (sort === "release") return b.releaseDate.localeCompare(a.releaseDate);
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [baseList, regions, years, genres, sort]);

  useEffect(() => {
    setPage(1);
  }, [type, keyword, regions, years, genres, sort]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedFilms = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggle = <T,>(value: T, list: T[], setList: (next: T[]) => void) => {
    setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  const resetFilters = () => {
    setRegions([]);
    setYears([]);
    setGenres([]);
  };

  const activeFilterCount = regions.length + years.length + genres.length;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视分类", to: "/category/all" },
          { label: categoryName },
        ]}
      />

      <header className="mt-6">
        <h1 className="font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
          {keyword ? `“${keyword}”的搜索结果` : meta ? meta.title : "全部影视作品"}
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {keyword
            ? `共为你找到 ${filtered.length} 部与关键词相关的影视作品，可继续使用左侧筛选缩小范围。`
            : meta
              ? meta.description
              : "浏览一路向西平台收录的全部影视作品，支持地区、年份与类型多选筛选，快速定位你想了解的故事。"}
        </p>
      </header>

      <div className="mt-8 flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24">
            <FilterPanel
              regions={regions}
              years={years}
              genres={genres}
              onToggleRegion={(value) => toggle(value, regions, setRegions)}
              onToggleYear={(value) => toggle(value, years, setYears)}
              onToggleGenre={(value) => toggle(value, genres, setGenres)}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
            <p className="text-sm text-foreground-600">
              共 <span className="font-semibold text-foreground-950">{filtered.length}</span> 部作品
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line text-base" />
                筛选
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-accent-500 px-1.5 text-xs text-background-50">{activeFilterCount}</span>
                )}
              </button>
              <div className="flex items-center gap-1 rounded-full bg-background-50 p-1">
                <button
                  type="button"
                  onClick={() => setSort("updated")}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                    sort === "updated" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  按更新时间
                </button>
                <button
                  type="button"
                  onClick={() => setSort("release")}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                    sort === "release" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  按上映时间
                </button>
              </div>
            </div>
          </div>

          {pagedFilms.length === 0 ? (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <p className="mt-4 text-sm text-foreground-600">没有符合条件的作品，试试减少筛选条件或更换关键词。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-refresh-line text-base" />
                重置筛选
              </button>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {pagedFilms.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          )}

          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-lg bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel
              regions={regions}
              years={years}
              genres={genres}
              onToggleRegion={(value) => toggle(value, regions, setRegions)}
              onToggleYear={(value) => toggle(value, years, setYears)}
              onToggleGenre={(value) => toggle(value, genres, setGenres)}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}